/**
 * AgentsPage — Sprint 21 KAIROS Pantheon view.
 *
 * Single-page roster of every named agent in the system with live status +
 * hit rate. Doubles as a health monitor.
 */

import { useState, useEffect } from 'react';
import { Users, Activity, Circle } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const STATUS_COLORS = {
  active:    '#10b981',
  idle:      '#f59e0b',
  degraded:  '#ef4444',
  inactive:  '#6b7280',
};

const PHASE_COLORS = {
  1: '#3b82f6',
  2: '#8b5cf6',
  3: '#10b981',
  4: '#f59e0b',
  'continuous':    '#06b6d4',
  'event-driven':  '#ef4444',
  'passive':       '#6b7280',
};

function Sparkline({ value, color }) {
  if (value == null) return null;
  const pct = Math.min(Math.max(value, 0), 100);
  return (
    <div className="w-full h-1 bg-korinth-bg rounded overflow-hidden">
      <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

function AgentCard({ agent }) {
  const statusColor = STATUS_COLORS[agent.status] || STATUS_COLORS.inactive;
  const phaseColor = PHASE_COLORS[agent.phase] || '#6b7280';
  const hitColor = agent.hit_rate_30d == null ? '#6b7280' : agent.hit_rate_30d >= 60 ? '#10b981' : agent.hit_rate_30d >= 50 ? '#f59e0b' : '#ef4444';

  const lastRunTxt = (() => {
    if (!agent.last_run) return 'Never run';
    const ageMs = Date.now() - new Date(agent.last_run).getTime();
    const min = Math.floor(ageMs / 60000);
    if (min < 1) return 'Just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  })();

  return (
    <div className="bg-korinth-surface border border-korinth-border rounded-lg p-4 hover:border-korinth-accent/40 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <Circle className="w-2 h-2" fill={statusColor} style={{ color: statusColor }} />
            <span className="text-lg font-mono font-bold tracking-widest text-white">{agent.name}</span>
            {agent.new_in_sprint && (
              <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-korinth-accent/15 text-korinth-accent">NEW · Sprint {agent.new_in_sprint}</span>
            )}
          </div>
          <div className="text-[10px] font-mono text-korinth-accent mt-0.5">{agent.role}</div>
        </div>
        <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded flex-shrink-0" style={{ color: phaseColor, backgroundColor: phaseColor + '15' }}>
          Phase {agent.phase}
        </span>
      </div>

      <div className="text-[10px] font-mono text-korinth-muted italic leading-relaxed mb-3">
        "{agent.tagline}"
      </div>

      <div className="space-y-1.5 text-[10px] font-mono">
        <div className="flex items-center justify-between">
          <span className="text-korinth-muted">Cadence</span>
          <span className="text-korinth-text">{agent.cadence}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-korinth-muted">Model</span>
          <span className="text-korinth-text">{agent.model_tier}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-korinth-muted">Last run</span>
          <span className="text-korinth-text">{lastRunTxt}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-korinth-muted">30d sample</span>
          <span className="text-korinth-text">{agent.sample_size || 0}</span>
        </div>
        {agent.cost_today_usd != null && (
          <div className="flex items-center justify-between">
            <span className="text-korinth-muted">Today's spend</span>
            <span className="text-korinth-text">${agent.cost_today_usd.toFixed(2)}</span>
          </div>
        )}
        {agent.hit_rate_30d != null && (
          <div className="flex items-center justify-between">
            <span className="text-korinth-muted">30d hit rate</span>
            <span className="font-bold" style={{ color: hitColor }}>
              {agent.hit_rate_30d}%
              {agent.sample_size > 0 && <span className="text-korinth-muted font-normal"> ({agent.sample_size})</span>}
            </span>
          </div>
        )}
        {agent.hit_rate_30d != null && (
          <Sparkline value={agent.hit_rate_30d} color={hitColor} />
        )}
        {(agent.status === 'inactive' || agent.status === 'idle') && agent.inactive_reason && (
          <div className="mt-1.5 pt-1.5 border-t border-korinth-border/30 text-[9px] font-mono text-korinth-muted italic leading-snug">
            <span className="uppercase tracking-wider not-italic mr-1">{agent.status === 'inactive' ? 'Inactive:' : 'Idle:'}</span>
            {agent.inactive_reason}
          </div>
        )}
        {agent.probe_primary && (
          <div className="text-[9px] font-mono text-korinth-muted/60 leading-snug">
            probe: {agent.probe_primary}
          </div>
        )}
      </div>
    </div>
  );
}

function FlowDiagram() {
  return (
    <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg p-4">
      <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-3">Orchestration Pipeline</div>
      <div className="space-y-3 text-[11px] font-mono">
        <div>
          <span className="font-bold text-blue-400">Phase 1 (Parallel):</span>
          <span className="text-korinth-text ml-2">HERMES · METIS · ATLAS · IRIS · HEPHAESTUS</span>
          <div className="text-[10px] text-korinth-muted ml-4">Specialist observations feed the synthesis stage.</div>
        </div>
        <div className="text-korinth-muted ml-2">↓</div>
        <div>
          <span className="font-bold text-purple-400">Phase 2 (Parallel):</span>
          <span className="text-korinth-text ml-2">PYTHIA · pattern retrieval</span>
          <div className="text-[10px] text-korinth-muted ml-4">Voices consensus + deterministic historical pattern matching (ARACHNE deprecated 22.6.6 P20).</div>
        </div>
        <div className="text-korinth-muted ml-2">↓</div>
        <div>
          <span className="font-bold text-korinth-accent">Phase 3 (Synthesis):</span>
          <span className="text-korinth-text ml-2">KAIROS</span>
          <div className="text-[10px] text-korinth-muted ml-4">Commander. Integrates all inputs into final observations, predictions, recommendations.</div>
        </div>
        <div className="text-korinth-muted ml-2">↓</div>
        <div>
          <span className="font-bold text-korinth-warning">Phase 4 (Post-synthesis):</span>
          <span className="text-korinth-text ml-2">CLOTHO</span>
          <div className="text-[10px] text-korinth-muted ml-4">Recomputes theme conviction from updated predictions.</div>
        </div>
        <div className="pt-3 border-t border-korinth-border/30">
          <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Continuous / Event-driven:</div>
          <div className="text-[11px] font-mono text-korinth-text">
            <span className="font-bold text-cyan-400">THEMIS</span> <span className="text-korinth-muted">(6h adjudication)</span> ·{' '}
            <span className="font-bold text-red-400">NEMESIS</span> <span className="text-korinth-muted">(event-triggered emergency cycle)</span> ·{' '}
            <span className="font-bold text-cyan-400">HEIMDALL</span> <span className="text-korinth-muted">(every signal scored)</span> ·{' '}
            <span className="font-bold text-korinth-muted">MNEMOSYNE</span> <span className="text-korinth-muted">(passive memory)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AgentsPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('agents'); }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const r = await fetch('/api/pantheon');
        if (r.ok) setData(await r.json());
      } catch {}
      setLoading(false);
    }
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  const agents = data?.agents || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Users className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">The Pantheon</h1>
        <span className="text-korinth-muted text-sm font-mono">
          {data ? `${data.total} specialized intelligences · ${data.active_count} active` : 'loading...'}
          {data?.degraded_count > 0 && <span className="text-korinth-danger ml-2">· {data.degraded_count} degraded</span>}
        </span>
      </div>

      <div className="bg-korinth-bg/50 border-l-4 border-l-korinth-accent border border-korinth-border/30 rounded-lg p-4 text-[11px] font-mono text-korinth-text leading-relaxed">
        <div className="flex items-center gap-2 mb-1">
          <Activity className="w-3.5 h-3.5 text-korinth-accent" />
          <span className="text-korinth-accent font-bold uppercase tracking-wider">KAIROS Pantheon</span>
        </div>
        Each named agent is a specialist feeding the KAIROS commander at Phase 3 synthesis.
        Greek mythology chosen so every agent has a semantic handle — future sessions, docs, and dashboards
        reference these names. Status lights update every 60s; hit rates compute from resolved
        <code className="px-1 mx-0.5 rounded bg-korinth-bg">kairos_predictions</code> with matching
        <code className="px-1 mx-0.5 rounded bg-korinth-bg">source_agent</code>.
      </div>

      {loading && !agents.length ? (
        <div className="text-[11px] font-mono text-korinth-muted text-center py-8 italic">Loading pantheon...</div>
      ) : (
        <ModuleContainer title={`The ${agents.length} Agents`} subtitle="Click status dot to see last run details (future sprint)">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map(a => <AgentCard key={a.name} agent={a} />)}
          </div>
        </ModuleContainer>
      )}

      <ModuleContainer title="Pipeline" subtitle="How the pantheon feeds KAIROS">
        <FlowDiagram />
      </ModuleContainer>
    </div>
  );
}
